import { Button } from "@benefit-systems/common-components";
import { useFormikContext } from "formik";
import _ from "lodash";
import { bool, func, object, shape, string } from "prop-types";
import { useState } from "react";

import EditActiveBenefitSingleForm from "web/Pages/Checkout/Subscription/BenefitSummary/BenefitForm/ChangeActiveBenefitDataForm/editActiveBenefitSingleForm";
import {
  hasName,
  hastLastname,
} from "web/Pages/Checkout/Subscription/BenefitSummary/BenefitForm/CompletedForm/utils";
import ChangeReceiverEmailForm from "web/Pages/Checkout/Subscription/BenefitSummary/BenefitForm/changeReceiverEmailForm";
import {
  FORM_TYPES,
  newDataIsSavedText,
  newEmailIsSavedText,
} from "web/Pages/Checkout/Subscription/BenefitSummary/BenefitForm/constants";
import { handleDataChangedCallback } from "web/Pages/Checkout/Subscription/BenefitSummary/BenefitForm/utils";
import DuplicateCardForm from "web/Pages/Checkout/Subscription/BenefitSummary/DuplicateCardForm/duplicateCardForm";

import __ from "web/Layout/Translations";

import useModalNotification from "web/hooks/useModalNotification";

import formatDate from "web/utils/data/parser/dateAndTime/formatDate";

import {
  BENEFIT_STATUS,
  DUPLICATE_CALL,
  DuplicateStatus,
} from "web/constants/benefits";
import formLabels from "web/constants/formLabels";

import { SubscriptionActiveFormFieldType } from "web/types/Subscription";

import classify from "web/classify";

import DuplicateAlreadyOrdered from "../DuplicateAlreadyOrdered/duplicateAlreadyOrdered";
import defaultClasses from "../completedForm.scss";

const ACTIVE_FORM_FIELDS_TO_PREVIEW = [
  SubscriptionActiveFormFieldType.FIRSTNAME,
  SubscriptionActiveFormFieldType.LASTNAME,
];

const CheckoutSubscriptionCompletedForm = ({
  fieldsStructured,
  fieldsConfiguration,
  classes,
  formId,
  benefitId,
  getLegend,
  receiverEmail,
  formUUID,
  benefitUUID,
  receiver,
  isMyBenefitsView,
  handleItemUpdate,
  isEditing,
  setEditingFormId,
  customOnEditBtnClick,
  handleToggleEditModal,
  setFormsEditedByUsers,
  formStatus,
  setDuplicateReceiverData,
  index,
  duplicateCardData,
  duplicateReceiverData,
  receiverId,
  showDuplicateForm,
  benefitFormsWithDuplicateStatus,
  setFormDuplicatInfo,
  doesEmailNeedToBeResend,
  onFormUpdateSucceeded,
  isUnderliningNeeded,
  willFormBeSent,
  benefitStatus,
  allowChangingPersonalData,
  isActiveForm,
  outerFormValues,
}) => {
  const [showModalNotification] = useModalNotification();
  const { resetForm, values } = useFormikContext();
  const initialFields = fieldsStructured.map(({ id, value, label, type }) => ({
    value: outerFormValues?.[id || type] || value,
    type,
    id: id || type,
    label: formLabels[type || id] || label,
  }));
  const [currentFields, setCurrentFields] = useState(initialFields);
  const [currentReceiverEmail, setCurrentReceiverEmail] =
    useState(receiverEmail);

  const handleDataChanged = (
    notificationText,
    updatedItem,
    newFormsEditedByUsers
  ) => {
    showModalNotification(notificationText, 2, () =>
      handleDataChangedCallback(
        handleToggleEditModal,
        updatedItem,
        newFormsEditedByUsers
      )
    );
    if (!isMyBenefitsView) setEditingFormId(null);
  };

  const handleReceiverEmailChange = (newEmail) => {
    let newFormsEditedByUsers;

    setFormsEditedByUsers((currentList) => {
      const newValue = currentList.includes(formUUID)
        ? currentList
        : [...currentList, formUUID];
      newFormsEditedByUsers = [...newValue];
      return newValue;
    });

    let updatedItem;
    if (handleItemUpdate && isMyBenefitsView) {
      handleItemUpdate((currentItem) => {
        const newItem = _.cloneDeep(currentItem);
        const activeFormIndex = currentItem.activeForms.findIndex(
          (activeForm) => activeForm.id === formId
        );
        updatedItem = _.set(
          newItem,
          `activeForms.${activeFormIndex}.receiverEmail`,
          newEmail
        );
        updatedItem = _.set(
          newItem,
          `activeForms.${activeFormIndex}.emailSentAt`,
          new Date()
        );
        return updatedItem;
      });
    } else {
      setCurrentReceiverEmail(newEmail);
    }

    handleDataChanged(newEmailIsSavedText, updatedItem, newFormsEditedByUsers);

    if (typeof onFormUpdateSucceeded === "function") {
      onFormUpdateSucceeded(
        benefitId,
        formId,
        FORM_TYPES.ACTIVE_FORMS,
        receiver
      );
    }
  };

  const singleFormDataChange = (newData) => {
    let updatedItem;

    let newFormsEditedByUsers;

    setFormsEditedByUsers((currentList) => {
      const newValue = currentList.includes(formUUID)
        ? currentList
        : [...currentList, formUUID];
      newFormsEditedByUsers = [...newValue];
      return newValue;
    });

    if (handleItemUpdate && isMyBenefitsView) {
      handleItemUpdate((currentItem) => {
        const newItem = _.cloneDeep(currentItem);
        const formIndex = currentItem.forms.findIndex(
          (form) => form.id === formId
        );
        updatedItem = _.set(newItem, `forms.${formIndex}.fields`, newData);
        return updatedItem;
      });
    } else {
      const newFields = currentFields.map((field) => {
        const newField = { ...field };
        newField.value = newData[field.id];

        return newField;
      });
      setCurrentFields(newFields);
    }

    handleDataChanged(newDataIsSavedText, updatedItem, newFormsEditedByUsers);

    if (typeof onFormUpdateSucceeded === "function") {
      onFormUpdateSucceeded(benefitId, formId, FORM_TYPES.FORMS, receiver);
    }
  };

  const handleEditButtonClick = () => {
    setEditingFormId(formId);
  };
  const formValues = currentFields.map((field) => {
    if (isActiveForm && !ACTIVE_FORM_FIELDS_TO_PREVIEW.includes(field.type)) {
      return null;
    }

    const label =
      typeof field.label === "object" ? field.label?.title : field.label;

    return (
      <p className={classes.dataParagraph} key={field.id}>
        {__(label)}:{" "}
        <strong>
          {["date", "birthDate"].includes(field.type)
            ? formatDate(field.value)
            : field.value}
        </strong>
      </p>
    );
  });

  const editDataButton = (
    <button
      type="button"
      className={`${classes.editButton} ${
        isUnderliningNeeded ? classes.errorBorder : ""
      }`}
      onClick={customOnEditBtnClick || handleEditButtonClick}
    >
      {doesEmailNeedToBeResend
        ? __("Wyślij ponownie formularz")
        : __("Edytuj dane")}
    </button>
  );

  const getEmailSentButNotCompletedFieldsPreview = (showName, showLastName) => (
    <>
      {showName && (
        <p className={classes.dataParagraph}>
          {__("Imię")}: <strong>-</strong>
        </p>
      )}
      {showLastName && (
        <p className={classes.dataParagraph}>
          {__("Nazwisko")}: <strong>-</strong>
        </p>
      )}
    </>
  );

  const receiverEmailComponent = (
    <p className={classes.dataParagraph}>
      {__("Email")}: <strong>{currentReceiverEmail}</strong>
    </p>
  );

  const handleCancelation = () => {
    // Active Forms have their own Formik context, so they can be resetted as a whole
    if (isActiveForm) {
      resetForm();
    } else {
      // There can be multiple Small Forms in one Formik context, so they need to be resetted by id
      const currentBenefitForms = values[benefitId].forms;
      let init = {};

      initialFields.forEach((field) => {
        init = {
          ...init,
          [field.type]: field.value,
        };
      });

      resetForm({
        values: {
          ...values,
          [benefitId]: {
            activeForms: {},
            forms: {
              ...currentBenefitForms,
              [formId]: {
                fields: init,
              },
            },
          },
        },
      });
    }
    setEditingFormId(null);
  };

  const editingView = (
    <>
      {willFormBeSent && isActiveForm ? (
        <ChangeReceiverEmailForm
          formId={formId}
          benefitId={benefitId}
          handleCancelation={handleCancelation}
          formUUID={formUUID}
          benefitUUID={benefitUUID}
          onSuccessChanged={handleReceiverEmailChange}
          receiverEmailFieldName={`${benefitId}.activeForms.${formId}.receiverEmail`}
          emailRepeatedFieldName={`${benefitId}.activeForms.${formId}.confirmEmail`}
          isMyBenefitsView={isMyBenefitsView}
        />
      ) : (
        <EditActiveBenefitSingleForm
          handleCancelation={handleCancelation}
          fieldsStructured={fieldsStructured}
          receiver={receiver}
          formId={formId}
          benefitId={benefitId}
          formUUID={formUUID}
          benefitUUID={benefitUUID}
          onSuccessChanged={singleFormDataChange}
          isMyBenefitsView={isMyBenefitsView}
          handleToggleEditModal={handleToggleEditModal}
          allowChangingPersonalData={allowChangingPersonalData}
        />
      )}
    </>
  );

  // TODO: change empty firstname & lastname fields
  const preview = (
    <>
      {currentReceiverEmail && receiverEmailComponent}
      {!fieldsStructured.length
        ? getEmailSentButNotCompletedFieldsPreview(
            hasName(fieldsConfiguration),
            hastLastname(fieldsConfiguration)
          )
        : formValues}
      {formStatus.isEditable ? editDataButton : null}
    </>
  );

  const currentActiveDuplicateID = `${index}-${formId}`;
  const duplicateView = duplicateCardData &&
    benefitFormsWithDuplicateStatus &&
    showDuplicateForm === DUPLICATE_CALL.from_stepTwo && (
      <span>
        {benefitFormsWithDuplicateStatus.duplicateOrderStatus ===
          DuplicateStatus.NEVER_ORDERED &&
          benefitStatus === BENEFIT_STATUS.STATUS_ACTIVE &&
          (duplicateReceiverData.index !== currentActiveDuplicateID ||
            duplicateReceiverData.receiver !== receiver) && (
            <Button
              type="button"
              variant="primary"
              onClick={() =>
                setDuplicateReceiverData({
                  showForm: true,
                  index: currentActiveDuplicateID,
                  receiver,
                })
              }
              disabled={duplicateReceiverData.showForm}
              className={classes.duplicateButton}
            >
              {__("Zamów duplikat")}
            </Button>
          )}
        {duplicateReceiverData.showForm &&
          duplicateReceiverData.index === currentActiveDuplicateID &&
          duplicateReceiverData.receiver === receiver && (
            <DuplicateCardForm
              duplicateCardData={duplicateCardData}
              receivers={receiverId}
              showDuplicateForm={showDuplicateForm}
              setDuplicateReceiverData={setDuplicateReceiverData}
              setFormDuplicatInfo={setFormDuplicatInfo}
              benefitFormsWithDuplicateStatus={benefitFormsWithDuplicateStatus}
            />
          )}
        {benefitFormsWithDuplicateStatus.duplicateOrderStatus ===
          DuplicateStatus.ALREADY_ORDERED && (
          <DuplicateAlreadyOrdered duplicateCardData={duplicateCardData} />
        )}
      </span>
    );

  return (
    <div className={isEditing ? classes.flex : ""}>
      {getLegend(!!isEditing)}
      <div className={classes.fields}>{isEditing ? editingView : preview}</div>
      {duplicateView}
    </div>
  );
};

CheckoutSubscriptionCompletedForm.propTypes = {
  isEditable: bool.isRequired,
  receiverEmail: string,
  receiver: string.isRequired,
  classes: shape({}),
  formId: string.isRequired,
  benefitId: string.isRequired,
  getLegend: func.isRequired,
  formUUID: string.isRequired,
  benefitUUID: string.isRequired,
  isMyBenefitsView: bool.isRequired,
  handleItemUpdate: func,
  isEditing: bool.isRequired,
  setEditingFormId: func.isRequired,
  customOnEditBtnClick: func,
  handleToggleEditModal: func,
  setFormsEditedByUsers: func.isRequired,
  formStatus: string.isRequired,
  doesEmailNeedToBeResend: bool,
  onFormUpdateSucceeded: func,
  willFormBeSent: bool.isRequired,
  allowChangingPersonalData: bool.isRequired,
  isActiveForm: bool.isRequired,
  outerFormValues: object,
};

CheckoutSubscriptionCompletedForm.defaultProps = {
  receiverEmail: null,
  handleItemUpdate: null,
  customOnEditBtnClick: null,
  handleToggleEditModal: null,
  doesEmailNeedToBeResend: false,
  onFormUpdateSucceeded: null,
  outerFormValues: null,
};

export default classify(defaultClasses)(CheckoutSubscriptionCompletedForm);
